import type { Seat } from '@campfire/engine';
import type { SpadesView, TeamHandResult } from '@campfire/game-spades';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { trickCenter, trickHand } from './trick-table.ts';

const bidText = (b: number | null | undefined) => (b === null || b === undefined ? '–' : b === 0 ? 'nil' : String(b));
const teamName = (ctx: TableCtx, t: 0 | 1) => `${name(ctx, t)} & ${name(ctx, t + 2)}`;

function handSummary(ctx: TableCtx, r: TeamHandResult, t: 0 | 1): string {
  const nils = r.nils.map((n) => ` ${name(ctx, n.seat)} ${n.made ? 'made' : 'missed'} nil.`).join('');
  const contract = r.bid > 0 ? `${r.made ? 'made' : 'set on'} ${r.bid} (took ${r.tricks})` : `took ${r.tricks}`;
  return `${teamName(ctx, t)} ${contract}: ${r.points >= 0 ? '+' : ''}${r.points}.${nils}${r.bagPenalty ? ' 10 bags: −100!' : ''}`;
}

export const spadesUi: GameUi = {
  id: 'spades',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as SpadesView;
    return `${s === v.dealer ? 'dealer · ' : ''}bid ${bidText(v.bids[s])} · ${v.won[s] ?? 0} tricks`;
  },
  render(ctx) {
    const v = ctx.msg.view as SpadesView;
    const me = v.you;
    const partner = me === null ? null : (me + 2) % 4;
    const team = (t: 0 | 1) => {
      const bid = [t, t + 2].reduce((n, s) => n + (v.bids[s] ?? 0), 0);
      const took = (v.won[t] ?? 0) + (v.won[t + 2] ?? 0);
      return h(
        `div.trick-score.team${me !== null && me % 2 === t ? '.mine' : ''}`,
        { 'data-testid': `team-${t}` },
        h('span.pname', {}, teamName(ctx, t)),
        h('span.big-num', {}, String(v.scores[t])),
        h('span.muted', {}, `${v.bags[t]} bag${v.bags[t] === 1 ? '' : 's'}${v.phase === 'play' ? ` · bid ${bid}, took ${took}` : ''}`),
      );
    };
    const tag = (s: Seat) => [s === partner ? 'partner' : null, v.bids[s] !== null ? `bid ${bidText(v.bids[s])}` : null, s === v.dealer ? 'D' : null].filter(Boolean).join(' · ') || null;
    const e = v.last;
    const fresh = e && v.phase === 'bid' && v.bids.every((b) => b === null);
    const bidding = v.phase === 'bid' && ctx.myTurn;
    return h(
      'div.trick-game.spades',
      {},
      h('div.trick-scores.teams', { 'data-testid': 'scores' }, team(0), team(1)),
      trickCenter(ctx, { trick: v.trick, lastTrick: v.lastTrick, seats: 4, turn: v.turn, tag }),
      h(
        'div.event',
        { 'data-testid': 'event' },
        fresh || (e && v.result) ? `Hand ${e.hand}: ${handSummary(ctx, e.teams[0], 0)} ${handSummary(ctx, e.teams[1], 1)}` : null,
        v.result
          ? null
          : v.phase === 'bid'
            ? ` ${v.turn === null ? '' : name(ctx, v.turn)} ${v.turn === ctx.you ? 'bid' : 'bids'} next.`
            : v.spadesBroken
              ? 'Spades are broken.'
              : 'Spades not broken yet.',
      ),
      bidding
        ? h(
            'div.trick-controls',
            {},
            h('div.hint', { 'data-testid': 'hint' }, 'How many tricks will you take?'),
            h(
              'div.bid-grid',
              { 'data-testid': 'bids' },
              v.bidChoices.map((b) => h(`button${b === 0 ? '.nil' : ''}`, { 'data-testid': `bid-${b}`, onclick: () => ctx.act({ type: 'bid', bid: b }) }, b === 0 ? 'Nil' : String(b))),
            ),
          )
        : ctx.myTurn
          ? h('div.hint', { 'data-testid': 'hint' }, 'Tap a highlighted card to play it.')
          : null,
      trickHand(ctx, v.hand, v.legal, (c) => ctx.act({ type: 'play', card: c.id }), { dimOthers: ctx.myTurn && v.phase === 'play' }),
      h('div.muted.trick-foot', {}, `Hand ${v.handNo} · spades are trumps · first to ${v.target}`),
    );
  },
};
